import { useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  ChevronDown,
  Search,
} from 'lucide-react';
import { PageLoader } from './Loader';

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100, 'All'];

function defaultSearchValue(row, key) {
  const value = row?.[key];
  return value === null || value === undefined ? '' : String(value);
}

function compareValues(a, b) {
  if (a === b) return 0;
  if (a === null || a === undefined) return -1;
  if (b === null || b === undefined) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

const TRUNCATE_AT = 30;

/** Plain string/number cell values longer than 30 chars are clipped with an ellipsis; the full value shows in a native tooltip on hover. */
function CellValue({ value }) {
  if (typeof value !== 'string' && typeof value !== 'number') return value;
  const text = String(value);
  if (text.length <= TRUNCATE_AT) return text;
  return <span title={text}>{text.slice(0, TRUNCATE_AT)}…</span>;
}

/**
 * Shared table primitive: loader, search, sortable columns, and a page-size-aware
 * pagination footer (5/10/25/50/100/All, default 10). Any page listing records
 * should render through this instead of a hand-rolled <table>.
 *
 * columns: [{ key, label, sortable = true, align, render(row, rowIndex), sortAccessor(row), searchValue(row) }]
 */
export function DataTable({
  columns,
  data = [],
  loading = false,
  searchPlaceholder = 'Search...',
  getRowKey = (row, index) => row.id ?? index,
  emptyMessage = 'No records found.',
  defaultPageSize = 10,
  defaultSort = null,
}) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(defaultSort);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return data;
    return data.filter((row) =>
      columns.some((col) => {
        const value = col.searchValue ? col.searchValue(row) : defaultSearchValue(row, col.key);
        return value.toLowerCase().includes(term);
      })
    );
  }, [data, search, columns]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const column = columns.find((c) => c.key === sort.key);
    if (!column) return filtered;
    const accessor = column.sortAccessor || ((row) => row[column.key]);
    const factor = sort.direction === 'desc' ? -1 : 1;
    return [...filtered].sort((a, b) => factor * compareValues(accessor(a), accessor(b)));
  }, [filtered, sort, columns]);

  const totalRows = sorted.length;
  const effectivePageSize = pageSize === 'All' ? totalRows || 1 : pageSize;
  const totalPages = Math.max(1, Math.ceil(totalRows / effectivePageSize));

  useEffect(() => {
    setPage(1);
  }, [search, pageSize, sort, data.length]);

  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * effectivePageSize;
  const pageRows = sorted.slice(startIndex, startIndex + effectivePageSize);

  const handleSort = (column) => {
    if (column.sortable === false) return;
    setSort((prev) => {
      if (!prev || prev.key !== column.key) return { key: column.key, direction: 'asc' };
      if (prev.direction === 'asc') return { key: column.key, direction: 'desc' };
      return null;
    });
  };

  return (
    <div className="w-full">
      <div className="flex flex-col gap-3 pb-3 sm:flex-row sm:items-center sm:justify-between">
       
        <div className="relative w-full sm:w-64 float-right">
          <Search size={16} className="pointer-events-none text-brand-500 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full border-brand-400 border-2 rounded-lg border  bg-white py-1.5 pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/15"
          />
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="divide-x divide-slate-200 border-b border-slate-200 bg-brand-100">
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col)}
                  className={`whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500 ${
                    col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                  } ${col.sortable === false ? '' : 'cursor-pointer select-none hover:text-brand-600'}`}
                >
                  <span className={`inline-flex items-center gap-1 ${col.align === 'right' ? 'flex-row-reverse' : ''}`}>
                    {col.label}
                    {col.sortable !== false &&
                      (sort?.key === col.key ? (
                        sort.direction === 'asc' ? (
                          <ChevronUp size={13} className="text-brand-500" />
                        ) : (
                          <ChevronDown size={13} className="text-brand-500" />
                        )
                      ) : (
                        <ChevronsUpDown size={13} className="text-brand-500" />
                      ))}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="py-10">
                  <PageLoader label="Loading..." />
                </td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-10 text-center text-sm text-slate-400">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              pageRows.map((row, rowIndexInPage) => {
                const absoluteIndex = startIndex + rowIndexInPage;
                return (
                  <tr
                    key={getRowKey(row, absoluteIndex)}
                    className="divide-x divide-slate-200 border-b border-slate-200 last:border-0 hover:bg-brand-50/40"
                  >
                    {columns.map((col, colIndex) => (
                      <td
                        key={col.key}
                        className={`px-4 py-3 ${colIndex === 0 ? 'text-brand-500 font-semibold' : 'text-slate-700'} ${
                          col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                        } ${col.className || ''}`}
                      >
                        <CellValue value={col.render ? col.render(row, absoluteIndex) : (row[col.key] ?? '—')} />
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
 
      {!loading && totalRows > 0 && (
        <div className="flex flex-col gap-3 pt-3 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between">
         
<label className="flex mt-3 items-center justify-between gap-2 text-sm text-slate-500">
          Show
          <select
            value={pageSize}
            onChange={(e) => {
              const value = e.target.value;
              setPageSize(value === 'All' ? 'All' : Number(value));
            }}
            className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm font-medium text-slate-700 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/15"
          >
            {PAGE_SIZE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          entries
        </label>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-slate-500 transition hover:border-brand-300 hover:text-brand-600 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Previous page"
            >
              <ChevronLeft size={16} />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push(`ellipsis-${p}`);
                acc.push(p);
                return acc;
              }, [])
              .map((p) =>
                typeof p === 'string' ? (
                  <span key={p} className="px-1 text-slate-300">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPage(p)}
                    className={`grid h-8 w-8 place-items-center rounded-lg text-sm font-medium transition ${
                      p === currentPage
                        ? 'bg-brand-600 text-white shadow-sm'
                        : 'text-slate-600 hover:bg-brand-50 hover:text-brand-600'
                    }`}
                  >
                    {p}
                  </button>
                )
              )}

            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-slate-500 transition hover:border-brand-300 hover:text-brand-600 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Next page"
            >
              <ChevronRight size={16} />
            </button>
          </div>
           <span>
            Showing {startIndex + 1} to {Math.min(startIndex + effectivePageSize, totalRows)} of {totalRows} entries
          </span>
        </div>
      )}
    </div>
  );
}

export default DataTable;
